import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { VEHICLE_TYPES, TRACKING_STATUSES, DEFAULT_MAP_CENTER } from '../../../constants';

const pickupIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:var(--bry-accent);color: var(--bry-text-main);width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);">📍</div>',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const dropoffIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:var(--bry-bg-secondary);color: var(--bry-text-main);width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);">🏁</div>',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const refPointIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:var(--bry-accent);color: var(--bry-text-main);width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);">📍</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const MOTO_SVG = `<svg width="14" height="10" viewBox="0 0 40 24" fill="white" style="display:block;">
  <circle cx="8" cy="18" r="5" stroke="white" stroke-width="2.5" fill="none"/>
  <circle cx="32" cy="18" r="5" stroke="white" stroke-width="2.5" fill="none"/>
  <path d="M8 18 L12 8 L22 8 L28 14 L32 14" stroke="white" stroke-width="2.5" fill="none"/>
  <path d="M18 8 L22 4 L28 4" stroke="white" stroke-width="2.5" fill="none"/>
</svg>`;

const CAR_SVG = `<svg width="14" height="10" viewBox="0 0 24 24" fill="white" style="display:block;">
  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.27-3.82c.07-.21.27-.35.49-.35h10.48c.22 0 .42.14.49.35L19 11H5z"/>
</svg>`;

const driverAvailableIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#00C853;color: var(--bry-text-main);width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.2);">${MOTO_SVG}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const driverBusyIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#333333;color: var(--bry-text-main);width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.2);">${MOTO_SVG}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const carAvailableIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#00C853;color: var(--bry-text-main);width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.2);">${CAR_SVG}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const carBusyIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#333333;color: var(--bry-text-main);width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.2);">${CAR_SVG}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const getDriverIcon = (status, vehicleType) => {
  if (vehicleType === VEHICLE_TYPES.CARRO) {
    return status === TRACKING_STATUSES.ONLINE ? carAvailableIcon : carBusyIcon;
  }
  return status === TRACKING_STATUSES.ONLINE ? driverAvailableIcon : driverBusyIcon;
};

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function FitBounds({ pickupPos, dropoffPos, driverPositions, refPoints }) {
  const map = useMap();
  useEffect(() => {
    const points = [];
    if (pickupPos) points.push([pickupPos.lat, pickupPos.lng]);
    if (dropoffPos) points.push([dropoffPos.lat, dropoffPos.lng]);
    if (refPoints?.length) {
      refPoints.forEach(rp => {
        if (rp.lat && rp.lng) points.push([rp.lat, rp.lng]);
      });
    }
    if (driverPositions?.length) {
      driverPositions.forEach(d => {
        if (d.lat && d.lng) points.push([d.lat, d.lng]);
      });
    }
    if (points.length > 1) {
      map.fitBounds(points, { padding: [50, 50] });
    } else if (points.length === 1) {
      map.setView(points[0], 14);
    }
  }, [pickupPos, dropoffPos, driverPositions, refPoints, map]);
  return null;
}

function ServiceMap({ pickupPos, dropoffPos, onPickupChange, onDropoffChange, drivers = [], height = '350px', refPoints = [], onRefPointAdd, refPointMode = false }) {
  const [clickMode, setClickMode] = useState('pickup');
  const [driverPositions, setDriverPositions] = useState(drivers);

  useEffect(() => {
    setDriverPositions(drivers);
  }, [drivers]);

  useEffect(() => {
    if (pickupPos && !dropoffPos) {
      setClickMode('dropoff');
    } else if (!pickupPos) {
      setClickMode('pickup');
    }
  }, [pickupPos, dropoffPos]);

  const handleMapClick = useCallback((coords) => {
    if (refPointMode) {
      if (onRefPointAdd) onRefPointAdd(coords);
      return;
    }
    if (clickMode === 'pickup') {
      onPickupChange(coords);
      setClickMode('dropoff');
    } else {
      onDropoffChange(coords);
      setClickMode('pickup');
    }
  }, [clickMode, onPickupChange, onDropoffChange, refPointMode, onRefPointAdd]);

  const defaultCenter = pickupPos
    ? [pickupPos.lat, pickupPos.lng]
    : [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng];

  return (
    <div style={{ position: 'relative', height, width: '100%' }}>
      <div style={{
        position: 'absolute', top: 10, left: 10, zIndex: 1000,
        background: 'rgba(255,255,255,0.95)', borderRadius: 12, padding: '6px 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: 13, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 6
      }}>
        {refPointMode ? (
          <span style={{ color: 'var(--bry-accent)' }}>📍 Añadir punto de referencia — toca el mapa</span>
        ) : (
          <>
            <span style={{ color: clickMode === 'pickup' ? 'var(--bry-accent)' : '#666', cursor: 'pointer' }}
              onClick={() => setClickMode('pickup')}>
              📍 Origen
            </span>
            <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
            <span style={{ color: clickMode === 'dropoff' ? 'var(--bry-accent)' : '#666', cursor: 'pointer' }}
              onClick={() => setClickMode('dropoff')}>
              🏁 Destino
            </span>
          </>
        )}
      </div>

      <MapContainer
        center={defaultCenter}
        zoom={14}
        style={{ height: '100%', width: '100%', borderRadius: 16, zIndex: 1 }}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickHandler onMapClick={handleMapClick} />
        <FitBounds pickupPos={pickupPos} dropoffPos={dropoffPos} driverPositions={driverPositions} refPoints={refPoints} />

        {pickupPos && (
          <Marker position={[pickupPos.lat, pickupPos.lng]} icon={pickupIcon}>
            <Popup>📍 Origen</Popup>
          </Marker>
        )}
        {dropoffPos && (
          <Marker position={[dropoffPos.lat, dropoffPos.lng]} icon={dropoffIcon}>
            <Popup>🏁 Destino</Popup>
          </Marker>
        )}
        {refPoints.map((rp, idx) => (
          <Marker key={idx} position={[rp.lat, rp.lng]} icon={refPointIcon}>
            <Popup>{rp.label || `Punto de referencia ${idx + 1}`}</Popup>
          </Marker>
        ))}

        {driverPositions.map((driver, idx) => (
          <Marker
            key={driver.driver_uid || idx}
            position={[driver.lat, driver.lng]}
            icon={getDriverIcon(driver.status, driver.vehicle_type)}
          >
            <Popup>
              <div style={{ textAlign: 'center', minWidth: 140 }}>
                {driver.avatar_url && (
                  <img
                    src={driver.avatar_url}
                    alt=""
                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', marginBottom: 6 }}
                  />
                )}
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {driver.name || 'Conductor'}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {driver.status === TRACKING_STATUSES.ONLINE ? '🟢 Disponible' : '🟠 Ocupado'}
                  {driver.vehicle_type && ` · ${driver.vehicle_type}`}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                  a {driver.distance_km ? `${driver.distance_km.toFixed(1)} km` : '—'}
                  {driver.rating ? ` · ⭐ ${driver.rating.toFixed(1)}` : ''}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

export default ServiceMap;
